import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags.js'
import * as nodes from '/vercel/path0/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Mendable Python SDK\ndescription: Learn how to use the Mendable Python SDK to add sources and ask questions to Mendable directly from your Python code. \n---\n\nThis is a Python SDK for Mendable.ai, which provides a Python interface to interact with Mendable.ai's API.\n\n# Mendable Python SDK\n\nThis is a Python SDK for Mendable.ai, which provides a Python interface to interact with Mendable.ai's API.\n\n## Installation\n\nTo install this package, use pip:\n\n```bash\npip install mendable-py\n```\n\n## Prerequisites\n\nTo use this package, you'll need to obtain an API key from Mendable.ai and make it available as an environment variable or set it in the constructor.\n\nIn your environment (add to .env file):\n\n```bash\nMENDABLE_API_KEY=your_api_key\n```\n\nOR\n\n```python\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n```\n\n\n## Basic Usage\n\nYou can use this package to add sources to Mendable and ask questions to it (history is an option parameter):\n\n```python\nfrom mendable import ChatApp\n\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n\nmy_chat_bot.add(\"url\", \"https://www.mendable.ai/\")\n\nanswer = my_chat_bot.ask(question=\"What is Mendable?\", history=[{ \"prompt\" : \"How do I create a new project?\", \"response\" : \"You can create a new project by going to the projects page and clicking the new project button.\" }])\n\nprint(answer['answer']['text'])\n```\n\nHere is what the ask methods response object looks like:\n\n```json\n{\n  \"answer\": {\n    \"text\": \"This is how to deploy it...\"\n  },\n  \"message_id\": 123,\n  \"sources\": [\n    {\n      \"id\": 866,\n      \"content\":\"\",\n      \"link\": \"\",\n      \"relevance_score\": 0.99\n    },\n  ]\n}\n```\n\n\n## Rate Message\n\nThis is how you can rate a message positive (1) or negative (0).\n\n```python\nfrom mendable import ChatApp\n\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n\nmy_chat_bot.add(\"url\", \"https://www.mendable.ai/\")\n\nanswer = my_chat_bot.ask(question=\"What is Mendable?\", history=[{ \"prompt\" : \"How do I create a new project?\", \"response\" : \"You can create a new project by going to the projects page and clicking the new project button.\" }])\n\nmessage_id = answer[\"message_id\"]\n\nmy_chat_bot.rate_message(message_id, 1)\n```\n\n## See all sources for project\n\nThis method lists all unique sources for a project.\n\n```python\nfrom mendable import ChatApp\n\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n\nmy_chat_bot.add(\"url\", \"https://www.mendable.ai/\")\n\nmy_chat_bot.get_sources()\n\n```\n\nThe response object looks like this:\n```json\n[\n  {\n    \"id\": 52,\n    \"source\": \"https://mendable.ai\"\n  },\n  ..\n]\n```\n\n## Add and Delete Indexes\n\nYou can also check/delete indexes using `get_sources` and `delete_source` functions:\n\n```python\nfrom mendable import ChatApp\n\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n\nmy_chat_bot.add(\"url\", \"https://www.mendable.ai/\")\n\nmy_chat_bot.get_sources()\n\nmy_chat_bot.delete_source(\"https://www.mendable.ai/\")\n```\n\n### Supported ingestion formats and type\n\n- Website Crawler URL -> \"website-crawler\"\n- Docusaurus site URL -> \"docusaurus\"\n- GitHub Repo URL -> \"github\"\n- YouTube Video URL -> \"youtube\"\n- Single Website URL -> \"url\"\n- Sitemap URL -> \"sitemap\"\n- OpenAPI YAML URL -> \"openapi\"\n\n## Start new conversation\n\nThis method makes a new conversation for a given project\n\n```python\nfrom mendable import ChatApp\n\nmy_chat_bot = ChatApp(api_key=\"your-api-key\")\n\nmy_chat_bot.start_new_conversation()\n\n```\n\n";
const filepath = "/sdks/python.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
